/**
 * Deterministic categorical colour per distinct placement `objectRef` — the
 * "colored by object type" the 2D map wants. `.WLD`/`.TBL` carry no semantic
 * type field (a hangar and a church are both just "some `TERRn-XX` object",
 * world-terrain.md §1), so the only real, non-fabricated category boundary
 * available is the baked model id itself: colouring by `objectRef` groups
 * every placement of the same building/prop under one colour, which is
 * exactly the signal world-terrain.md §3's clustering pass already found
 * meaningful (repeated object ids forming tight "towns"). A `null` ref
 * (unresolved directory slot, §1.1 — not a decode error) gets a fixed
 * neutral colour rather than joining the hash space.
 */

export interface HslColor {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

export const UNRESOLVED_COLOR: HslColor = { h: 0, s: 0, l: 45 };

/** FNV-1a over the ref string — stable across runs/platforms, good-enough distribution for a few dozen buckets. */
function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

const SATURATION = 62;
const LIGHTNESS = 58;

/** Maps a baked `objectRef` (or `null` for an unresolved placement) to a stable HSL colour. */
export function colorForObjectRef(objectRef: string | null): HslColor {
  if (objectRef === null) return UNRESOLVED_COLOR;
  const hue = hashString(objectRef) % 360;
  return { h: hue, s: SATURATION, l: LIGHTNESS };
}

export function hslToCss(color: HslColor): string {
  return `hsl(${color.h}deg ${color.s}% ${color.l}%)`;
}

/** Same hue/lightness math, returned as an sRGB `[0,1]` triple for three.js `Color.setHSL`. */
export function hslToUnitTriple(color: HslColor): readonly [number, number, number] {
  return [color.h / 360, color.s / 100, color.l / 100];
}
